import { movePageSelection, thumbnailScale } from './pdfHelpers';
import type { PdfDocumentProxy, PdfPageProxy } from './pdfRasterize';
import { t } from './i18n';

const THUMB_SIZE = 150;

type RenderTask = ReturnType<PdfPageProxy['render']>;

function fill(template: string, values: Record<string, string | number>): string {
  return Object.entries(values).reduce((text, [key, value]) => text.split(`{${key}}`).join(String(value)), template);
}

function isMobileViewport(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches;
}

function chromeButton(label: string, variant: 'primary' | 'ghost'): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.textContent = label;
  Object.assign(btn.style, {
    minHeight: '40px',
    minWidth: '64px',
    padding: '0 16px',
    borderRadius: '12px',
    border: '0',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    color: variant === 'primary' ? '#fff' : 'var(--vs-ink, #fff)',
    background: variant === 'primary' ? 'var(--vs-accent, #007aff)' : 'var(--vs-surface-2, rgba(255,255,255,0.12))',
  } as CSSStyleDeclaration);
  return btn;
}

/**
 * Ask which page of a multi-page PDF to import. Thumbnails render lazily (as they scroll into
 * view), one at a time through the already-open document. Resolves the 1-based page, or null
 * when the shopper cancels.
 */
export function pickPdfPage(doc: PdfDocumentProxy): Promise<number | null> {
  const total = doc.numPages;
  const mobile = isMobileViewport();
  const previousFocus = document.activeElement as HTMLElement | null;

  return new Promise((resolve) => {
    let selected = 1;
    let closed = false;

    // --- DOM scaffold -------------------------------------------------------
    const overlay = document.createElement('div');
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', t('Choose a page'));
    overlay.tabIndex = -1;
    Object.assign(overlay.style, {
      position: 'fixed', inset: '0', zIndex: '999999', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(9,9,11,0.72)', backdropFilter: 'blur(2px)', padding: mobile ? '0' : '24px', boxSizing: 'border-box',
    } as CSSStyleDeclaration);

    const panel = document.createElement('div');
    Object.assign(panel.style, {
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
      background: 'var(--vs-surface, #18181b)', color: 'var(--vs-ink, #fff)',
      border: '1px solid var(--vs-line, rgba(255,255,255,0.08))', boxShadow: '0 24px 70px rgba(0,0,0,0.55)',
      width: mobile ? '100%' : 'min(92vw, 760px)', height: mobile ? '100%' : 'min(88vh, 640px)',
      borderRadius: mobile ? '0' : '18px',
    } as CSSStyleDeclaration);
    overlay.appendChild(panel);

    const header = document.createElement('div');
    Object.assign(header.style, {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', padding: '12px 16px',
      paddingTop: mobile ? 'max(12px, env(safe-area-inset-top))' : '12px',
      borderBottom: '1px solid var(--vs-line, rgba(255,255,255,0.08))', flexShrink: '0',
    } as CSSStyleDeclaration);
    const cancelBtn = chromeButton(t('Cancel'), 'ghost');
    const title = document.createElement('span');
    title.textContent = t('Choose a page');
    Object.assign(title.style, { fontSize: '14px', fontWeight: '600' } as CSSStyleDeclaration);
    const importBtn = chromeButton('', 'primary');
    header.append(cancelBtn, title, importBtn);

    const grid = document.createElement('div');
    grid.setAttribute('role', 'listbox');
    grid.setAttribute('aria-label', t('Choose a page'));
    Object.assign(grid.style, {
      flex: '1', overflowY: 'auto', display: 'grid', gap: '12px', padding: '16px',
      gridTemplateColumns: `repeat(auto-fill, minmax(${mobile ? 120 : THUMB_SIZE}px, 1fr))`, alignContent: 'start',
      paddingBottom: mobile ? 'max(16px, env(safe-area-inset-bottom))' : '16px',
    } as CSSStyleDeclaration);
    panel.append(header, grid);

    // --- Tiles ---------------------------------------------------------------
    const tiles: HTMLButtonElement[] = [];
    const frames: HTMLDivElement[] = [];
    for (let n = 1; n <= total; n++) {
      const tile = document.createElement('button');
      tile.type = 'button';
      tile.setAttribute('role', 'option');
      tile.dataset.page = String(n);
      Object.assign(tile.style, {
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', padding: '8px',
        borderRadius: '12px', border: '2px solid transparent', background: 'transparent', cursor: 'pointer',
        color: 'var(--vs-ink-2, rgba(255,255,255,0.75))', fontSize: '12px', fontWeight: '600',
      } as CSSStyleDeclaration);
      const frame = document.createElement('div');
      Object.assign(frame.style, {
        width: '100%', aspectRatio: '3 / 4', display: 'flex', alignItems: 'center', justifyContent: 'center',
        borderRadius: '8px', overflow: 'hidden',
        // Checker so transparent artwork stays visible.
        backgroundColor: '#f2f2f7',
        backgroundImage: 'conic-gradient(#dcdce1 25%, transparent 0 50%, #dcdce1 0 75%, transparent 0)',
        backgroundSize: '12px 12px',
      } as CSSStyleDeclaration);
      const label = document.createElement('span');
      label.textContent = fill(t('Page {n}'), { n });
      tile.append(frame, label);
      tile.addEventListener('click', () => select(n));
      tile.addEventListener('dblclick', () => finish(n));
      tiles.push(tile);
      frames.push(frame);
      grid.appendChild(tile);
    }

    function select(n: number): void {
      selected = n;
      tiles.forEach((tile, index) => {
        const active = index + 1 === n;
        tile.setAttribute('aria-selected', String(active));
        tile.style.borderColor = active ? 'var(--vs-accent, #007aff)' : 'transparent';
        tile.style.color = active ? 'var(--vs-ink, #fff)' : 'var(--vs-ink-2, rgba(255,255,255,0.75))';
      });
      importBtn.textContent = fill(t('Import page {n}'), { n });
      tiles[n - 1]?.scrollIntoView({ block: 'nearest' });
    }

    // --- Lazy thumbnails, rendered one at a time -----------------------------
    const pending: number[] = [];
    const requested = new Set<number>();
    const canvases: HTMLCanvasElement[] = [];
    let current: RenderTask | null = null;
    let pumping: Promise<void> | null = null;

    async function renderThumb(n: number): Promise<void> {
      const page = await doc.getPage(n);
      if (closed) return;
      const base = page.getViewport({ scale: 1 });
      const ratio = Math.min(2, window.devicePixelRatio || 1);
      const viewport = page.getViewport({ scale: thumbnailScale(base.width, base.height, THUMB_SIZE * ratio) });
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.ceil(viewport.width));
      canvas.height = Math.max(1, Math.ceil(viewport.height));
      const context = canvas.getContext('2d');
      if (!context) return;
      Object.assign(canvas.style, { maxWidth: '100%', maxHeight: '100%', display: 'block' } as CSSStyleDeclaration);
      current = page.render({ canvas, canvasContext: context, viewport, intent: 'print', background: 'rgba(0,0,0,0)' });
      try {
        await current.promise;
      } finally {
        current = null;
      }
      if (closed) return;
      canvases.push(canvas);
      frames[n - 1].replaceChildren(canvas);
    }

    function pump(): void {
      if (pumping || closed) return;
      pumping = (async () => {
        while (pending.length && !closed) {
          const n = pending.shift()!;
          await renderThumb(n).catch(() => undefined);
        }
        pumping = null;
      })();
    }

    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const n = Number((entry.target as HTMLElement).dataset.page);
        observer.unobserve(entry.target);
        if (requested.has(n)) continue;
        requested.add(n);
        pending.push(n);
      }
      pending.sort((a, b) => a - b);
      pump();
    }, { root: grid, rootMargin: '200px' });
    tiles.forEach((tile) => observer.observe(tile));

    // --- Close ---------------------------------------------------------------
    async function finish(result: number | null): Promise<void> {
      if (closed) return;
      closed = true;
      observer.disconnect();
      window.removeEventListener('keydown', onKey, true);
      current?.cancel();
      // The caller destroys the document next; let the in-flight thumbnail settle first.
      await pumping?.catch(() => undefined);
      canvases.forEach((canvas) => { canvas.width = 0; canvas.height = 0; });
      overlay.remove();
      if (previousFocus?.isConnected) previousFocus.focus({ preventScroll: true });
      resolve(result);
    }

    function columns(): number {
      const top = tiles[0]?.offsetTop ?? 0;
      return Math.max(1, tiles.filter((tile) => tile.offsetTop === top).length);
    }

    // Captured and stopped so designer shortcuts never act behind the dialog.
    function onKey(event: KeyboardEvent): void {
      event.stopPropagation();
      if (event.key === 'Escape') { event.preventDefault(); void finish(null); return; }
      if (event.key === 'Enter') {
        const target = event.target as HTMLElement | null;
        if (target === cancelBtn) return; // let the button click
        event.preventDefault();
        void finish(selected);
        return;
      }
      const next = movePageSelection(selected, event.key, total, columns());
      if (next !== selected) {
        event.preventDefault();
        select(next);
        tiles[next - 1].focus({ preventScroll: true });
      }
    }

    cancelBtn.addEventListener('click', () => void finish(null));
    importBtn.addEventListener('click', () => void finish(selected));
    overlay.addEventListener('pointerdown', (event) => {
      if (event.target === overlay) void finish(null);
    });
    window.addEventListener('keydown', onKey, true);

    select(1);
    document.body.appendChild(overlay);
    tiles[0].focus({ preventScroll: true });
  });
}
